<template>
  <div class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center px-6 pt-[140px] pb-6 overflow-y-auto z-[1500]">
    <div class="glass-strong rounded-xl p-5 w-full max-w-5xl mx-4 max-h-[calc(100vh-180px)] overflow-y-auto">
      <h3 class="text-xl font-bold text-white mb-1">Create a Shipping Label</h3>
      <p class="text-slate-400 text-xs mb-4">
        {{ make }} • {{ model }} — <span class="font-mono">{{ serial }}</span>
      </p>

      <div class="grid gap-6 text-sm grid-cols-2">
        <div class="mt-[5px]">
          <div class="mb-4">
            <h4 class="text-slate-300 font-semibold mb-2">Project</h4>
            <div class="grid grid-cols-[1fr_180px] gap-2">
              <div>
                <label class="block text-slate-300 text-xs mb-1">
                  Project Name
                  <span class="text-slate-500 text-[10px] ml-1">OPTIONAL</span>
                </label>
                <input
                  v-model="projectName"
                  placeholder="Project Name"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-full"
                />
              </div>
              <div>
                <label class="block text-slate-300 text-xs mb-1">Purpose</label>
                <select
                  v-model="purpose"
                  class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-full"
                >
                  <option value="poc">Proof of Concept</option>
                  <option value="test">Test</option>
                  <option value="tech_eval">Tech Eval</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </div>
            <div class="mt-2 flex items-center gap-2 rounded border border-slate-700/70 bg-slate-900/40 px-3 py-2">
              <input id="shipping-watchlist" v-model="watchlist" type="checkbox" class="accent-primary-500" />
              <label for="shipping-watchlist" class="text-xs text-slate-200">Watchlist</label>
            </div>
          </div>
          <h4 class="text-slate-300 font-semibold mb-2">Consignee</h4>
          <div class="grid grid-cols-2 gap-2">
            <div class="relative">
              <input
                v-model="consigneeQuery"
                placeholder="Consignee Name"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-full"
                @focus="showConsigneeDropdown = true"
                @blur="onConsigneeQueryBlur"
                @input="onConsigneeQueryInput"
              />
              <div
                v-if="showConsigneeDropdown"
                class="absolute z-30 mt-1 w-full rounded border border-slate-700 bg-slate-900/95 shadow-xl max-h-56 overflow-y-auto"
              >
                <button
                  type="button"
                  class="w-full text-left px-3 py-2 text-sm text-primary-300 hover:bg-slate-800/80 border-b border-slate-800"
                  @mousedown.prevent="selectCreateNewConsignee"
                >
                  + Create New Consignee
                </button>
                <button
                  v-for="consignee in filteredConsignees"
                  :key="consignee.id"
                  type="button"
                  class="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/80 border-b last:border-b-0 border-slate-800"
                  @mousedown.prevent="selectExistingConsignee(consignee)"
                >
                  <div class="font-medium">{{ consignee.name || 'Unnamed consignee' }}</div>
                  <div class="text-[11px] text-slate-400">
                    {{ consignee.organization_name || 'No org' }} • {{ consignee.email || 'No email' }}
                  </div>
                </button>
              </div>
            </div>
            <input
              v-model="organization"
              placeholder="Organization"
              class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
              :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
            />
            <input
              v-model="email"
              placeholder="Email"
              class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
              :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
            />
            <input
              v-model="phone"
              placeholder="Phone"
              class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
              :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
            />
            <div class="col-span-2 flex items-center gap-2">
              <input
                v-model="address1"
                placeholder="Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white flex-1 min-w-0"
                :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
              />
              <input
                v-model="address2"
                placeholder="Address 2"
                class="px-2 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-[105px]"
                :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
              />
            </div>
            <div class="col-span-2 flex items-center gap-2">
              <input
                v-model="city"
                placeholder="City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white flex-1 min-w-0"
                :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
              />
              <select
                v-model="state"
                class="px-2 py-2 bg-slate-800 border border-slate-700 rounded text-white uppercase w-[72px] appearance-none"
                :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
              >
                <option value="" disabled>ST</option>
                <option v-for="abbr in US_STATE_ABBREVIATIONS" :key="`to-${abbr}`" :value="abbr">
                  {{ abbr }}
                </option>
              </select>
              <input
                v-model="zip"
                placeholder="ZIP"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-[115px]"
                :disabled="!isCreatingNewConsignee && !!selectedConsigneeId"
              />
            </div>
          </div>

          <div v-if="selectedRate || trackingNumber || labelUrl" class="mt-4 flex justify-center">
            <div class="w-full max-w-md p-3 rounded border border-slate-800 bg-slate-900/40">
              <h4 class="text-slate-300 font-semibold mb-1 text-center">Shipping Label</h4>
              <div class="text-xs text-slate-300 space-y-1">
                <div v-if="selectedRate" class="text-center text-slate-200">
                  Selected: {{ selectedRate.carrier }} {{ selectedRate.service }} ·
                  ${{ (selectedRate.costCents / 100).toFixed(2) }}
                </div>
                <div v-if="trackingNumber" class="text-center">
                  Tracking: <span class="font-mono text-white">{{ trackingNumber }}</span>
                </div>
                <div v-if="labelUrl" class="flex items-center justify-center gap-3">
                  <a :href="labelUrl" target="_blank" download class="text-primary-300 underline"
                    >View / Download Label</a
                  >
                  <button
                    class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white text-[11px]"
                    @click="printUrl(labelUrl)"
                  >
                    Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="flex items-center gap-2 mb-2">
            <input type="checkbox" v-model="useContactForShipping" id="useContact" />
            <label for="useContact" class="text-slate-300 text-xs"
              >Use Consignee address for shipping</label
            >
          </div>
          <h4 class="text-slate-300 font-semibold mb-2">Shipping</h4>
          <div class="grid grid-cols-2 gap-2">
            <div class="col-span-2 flex items-center gap-2">
              <input
                v-model="shipAddress1"
                placeholder="Address 1"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white flex-1 min-w-0"
              />
              <input
                v-model="shipAddress2"
                placeholder="Address 2"
                class="px-2 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-[105px]"
              />
            </div>
            <div class="col-span-2 flex items-center gap-2">
              <input
                v-model="shipCity"
                placeholder="City"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white flex-1 min-w-0"
              />
              <select
                v-model="shipState"
                class="px-2 py-2 bg-slate-800 border border-slate-700 rounded text-white uppercase w-[72px] appearance-none"
              >
                <option value="" disabled>ST</option>
                <option v-for="abbr in US_STATE_ABBREVIATIONS" :key="`ship-${abbr}`" :value="abbr">
                  {{ abbr }}
                </option>
              </select>
              <input
                v-model="shipZip"
                placeholder="ZIP"
                class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white w-[115px]"
              />
            </div>
          </div>
          <h4 class="text-slate-300 font-semibold mb-1 mt-3">Package</h4>
          <div class="grid grid-cols-4 gap-2">
            <div class="col-span-2 flex items-center gap-1">
              <input
                v-model.number="weightLbs"
                type="number"
                min="0.1"
                step="0.1"
                placeholder="Wt"
                class="px-2 py-1.5 bg-slate-800/70 border border-slate-700 rounded text-white text-xs w-full"
              />
              <span class="text-slate-500 text-[10px] whitespace-nowrap">lbs</span>
            </div>
            <div class="col-span-2">
              <select
                v-model="carrierFilter"
                class="px-2 py-1.5 bg-slate-800/70 border border-slate-700 rounded text-white text-xs w-full"
              >
                <option value="">All Carriers</option>
                <option v-for="c in availableCarriers" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-3 gap-2 mt-1">
            <div class="flex items-center gap-1">
              <input
                v-model.number="lengthIn"
                type="number"
                min="1"
                step="0.5"
                placeholder="L"
                class="px-2 py-1.5 bg-slate-800/70 border border-slate-700 rounded text-white text-xs w-full"
              />
              <span class="text-slate-500 text-[10px]">L</span>
            </div>
            <div class="flex items-center gap-1">
              <input
                v-model.number="widthIn"
                type="number"
                min="1"
                step="0.5"
                placeholder="W"
                class="px-2 py-1.5 bg-slate-800/70 border border-slate-700 rounded text-white text-xs w-full"
              />
              <span class="text-slate-500 text-[10px]">W</span>
            </div>
            <div class="flex items-center gap-1">
              <input
                v-model.number="heightIn"
                type="number"
                min="1"
                step="0.5"
                placeholder="H"
                class="px-2 py-1.5 bg-slate-800/70 border border-slate-700 rounded text-white text-xs w-full"
              />
              <span class="text-slate-500 text-[10px]">H (in)</span>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-2">
            <button
              class="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-xs"
              @click.prevent="getRates"
              :disabled="gettingRates"
            >
              {{ gettingRates ? 'Getting Rates…' : 'Get Rates' }}
            </button>
            <span v-if="rates.length" class="text-slate-400 text-xs"
              >Select a rate on the right</span
            >
          </div>

          <div v-if="returnTracking || returnLabelUrl" class="mt-[46px] flex justify-center">
            <div class="w-full max-w-md p-3 rounded border border-slate-800 bg-slate-900/40">
              <h4 class="text-slate-300 font-semibold mb-1 text-center">Return Label</h4>
              <div class="text-xs text-slate-300 space-y-1">
                <div v-if="returnTracking" class="text-center">
                  Return Tracking: <span class="font-mono text-white">{{ returnTracking }}</span>
                </div>
                <div v-if="returnLabelUrl" class="flex items-center justify-center gap-3">
                  <a :href="returnLabelUrl" target="_blank" download class="text-primary-300 underline"
                    >View / Download Return Label</a
                  >
                  <button
                    class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white text-[11px]"
                    @click="printUrl(returnLabelUrl)"
                  >
                    Print Return
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-[36px] flex gap-3 items-center">
        <button class="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white" @click="$emit('close')">Close</button>
        <button v-if="!completed" class="px-3 py-2 rounded btn-primary" @click="save" :disabled="purchasing">
          {{ purchasing ? 'Purchasing…' : 'Purchase Shipping Label + Create Scan-Based Return' }}
        </button>
        <span v-if="completed" class="text-primary-300 text-xs">
          <template v-if="returnTracking">
            Outbound{{ outboundCostCents ? ` ($${(outboundCostCents / 100).toFixed(2)})` : '' }}
            + return{{ returnCostCents ? ` (est. $${(returnCostCents / 100).toFixed(2)})` : '' }}
            labels created — return is pay-on-use, charged only when scanned
          </template>
          <template v-else>
            Outbound label created{{ outboundCostCents ? ` — $${(outboundCostCents / 100).toFixed(2)}` : '' }}
          </template>
        </span>
      </div>
      <div v-if="message" class="text-slate-300 text-xs mt-2">{{ message }}</div>
      <div v-if="shippingModeWarning" class="text-amber-300 text-xs mt-2">
        {{ shippingModeWarning }}
      </div>
      <div v-if="error" class="text-red-400 text-xs mt-2">{{ error }}</div>
    </div>

    <!-- Micro-interaction: spring-in rates modal -->
    <transition name="rates-pop">
      <div
        v-if="showRatesModal"
        class="fixed inset-0 bg-black/45 backdrop-blur-sm flex items-center justify-center z-[65]"
        @click.self="showRatesModal = false"
      >
        <div class="glass-strong rounded-xl p-4 w-full max-w-2xl mx-4 border border-primary-500/30">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span class="text-primary-300 animate-pulse">✦</span>
              <h4 class="text-slate-100 font-semibold">Rates</h4>
            </div>
            <div class="flex items-center gap-2">
              <button
                class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white text-[11px]"
                @click.prevent="getRates"
                :disabled="gettingRates"
              >
                {{ gettingRates ? 'Getting…' : 'Refresh' }}
              </button>
              <button
                class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white text-[11px]"
                @click="showRatesModal = false"
              >
                Close
              </button>
            </div>
          </div>
          <p class="text-[11px] text-slate-400 mb-2">
            Select a rate, then close this panel to continue.
          </p>
          <div
            v-if="rates.length"
            class="max-h-64 overflow-auto divide-y divide-slate-800 rounded border border-slate-800"
          >
            <label
              v-for="r in rates"
              :key="r.rateId"
              class="flex items-center justify-between px-3 py-2 text-xs text-slate-300 gap-3"
            >
              <div class="flex items-center gap-3">
                <input
                  type="radio"
                  name="rate"
                  :value="r.rateId"
                  v-model="selectedRateId"
                />
                <span class="font-mono text-slate-200">{{ r.carrier }}</span>
                <span class="uppercase">{{ r.service }}</span>
                <span>{{ r.etaDays }}d</span>
              </div>
              <div class="font-semibold text-white">${{ (r.costCents / 100).toFixed(2) }}</div>
            </label>
          </div>
          <div v-else class="text-xs text-slate-500 py-4">
            {{ gettingRates ? 'Fetching rates…' : 'No rates yet. Click Refresh to fetch.' }}
          </div>

          <div v-if="carrierMeta" class="mt-2 p-2 rounded bg-slate-900/50 border border-slate-800 text-[10px] text-slate-500 space-y-0.5">
            <div class="flex items-center gap-3">
              <span>Carriers:</span>
              <span :class="carrierMeta.uspsConfigured ? 'text-emerald-400' : 'text-slate-600'">
                USPS {{ carrierMeta.uspsConfigured ? '✓' : '✗' }}
              </span>
              <span :class="carrierMeta.upsConfigured ? 'text-emerald-400' : 'text-slate-600'">
                UPS {{ carrierMeta.upsConfigured ? '✓' : '✗' }}
              </span>
              <span :class="carrierMeta.fedexConfigured ? 'text-emerald-400' : 'text-slate-600'">
                FedEx {{ carrierMeta.fedexConfigured ? '✓' : '✗' }}
              </span>
            </div>
            <div v-if="importantMessages.length" class="mt-0.5">
              <div v-for="(msg, i) in importantMessages" :key="i"
                :class="msg.isError ? 'text-rose-400' : 'text-amber-400/70'"
              >
                {{ msg.source }}: {{ msg.text }}
              </div>
            </div>
          </div>

          <div class="mt-3 flex items-center justify-end gap-2">
            <button
              class="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-white text-[11px]"
              @click="showRatesModal = false"
            >
              Close
            </button>
            <button
              class="px-2 py-1 rounded btn-primary text-[11px] disabled:opacity-60"
              :disabled="!selectedRateId"
              @click="confirmSelectedRate"
            >
              Select Rate
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import {
  fetchShippingRates,
  purchaseShippingLabel,
  getWarehouseAddress,
  type ShippingAddress,
  type ShippingParcel,
} from '@/lib/appApi'
const props = defineProps<{ make: string; model: string; serial: string; checkoutId?: number; equipmentId?: number }>()
const emit = defineEmits(['close', 'saved'])
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

const contactName = ref('')
const organization = ref('')
const projectName = ref('')
const purpose = ref<'poc' | 'test' | 'tech_eval' | 'maintenance'>('poc')
const watchlist = ref(false)
const email = ref('')
const phone = ref('')
const address1 = ref('')
const address2 = ref('')
const city = ref('')
const state = ref('')
const zip = ref('')
const consigneeQuery = ref('')
const selectedConsigneeId = ref<number | null>(null)
const selectedConsigneeOrganizationId = ref<number | null>(null)
const showConsigneeDropdown = ref(false)
const isCreatingNewConsignee = ref(true)
const consigneeDirectory = ref<
  Array<{
    id: number
    organization_id: number | null
    name: string | null
    email: string | null
    phone: string | null
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    zip: string | null
    shipping_same_as_consignee: boolean | null
    shipping_address1: string | null
    shipping_address2: string | null
    shipping_city: string | null
    shipping_state: string | null
    shipping_zip: string | null
    organization_name: string | null
  }>
>([])

const useContactForShipping = ref(false)
const shipAddress1 = ref('')
const shipAddress2 = ref('')
const shipCity = ref('')
const shipState = ref('')
const shipZip = ref('')

const message = ref('')
const error = ref('')
const shippingModeWarning = ref('')

// Rates + purchase state
const rates = ref<
  Array<{
    carrier: string
    service: string
    etaDays: number
    costCents: number
    currency: string
    rateId: string
  }>
>([])
const gettingRates = ref(false)
const selectedRateId = ref('')
const purchasing = ref(false)
const trackingNumber = ref('')
const labelUrl = ref('')
const completed = ref(false)

/** Package dimensions in imperial (user-facing) — auto-populated from equipment catalog */
const weightLbs = ref<number>(1)
const lengthIn = ref<number>(10)
const widthIn = ref<number>(8)
const heightIn = ref<number>(6)
const carrierFilter = ref<string>('')

/** Convert imperial to metric for the Shippo API */
const weightKg = computed(() => +(weightLbs.value * 0.453592).toFixed(2))
const lengthCm = computed(() => +(lengthIn.value * 2.54).toFixed(1))
const widthCm = computed(() => +(widthIn.value * 2.54).toFixed(1))
const heightCm = computed(() => +(heightIn.value * 2.54).toFixed(1))

/** Unique carrier names from the last rates fetch, for the filter dropdown */
const availableCarriers = computed(() => {
  const carriers = new Set(allRates.value.map((r) => r.carrier))
  return [...carriers].sort()
})

/** Holds the unfiltered rate list from the API; `rates` is the filtered view */
const allRates = ref<typeof rates.value>([])
const showRates = ref(false)
const showResult = ref(false)
const showRatesModal = ref(false)
const returnTracking = ref('')
const returnLabelUrl = ref('')
/** Outbound label cost in cents (charged immediately) */
const outboundCostCents = ref<number | null>(null)
/** Estimated return label cost in cents (scan-based: charged only when carrier scans) */
const returnCostCents = ref<number | null>(null)

/** Warehouse ship-from address loaded from app.settings */
const warehouseAddress = ref<ShippingAddress | null>(null)

/** Carrier diagnostics returned from the Edge Function */
const carrierMeta = ref<{
  shippoConfigured?: boolean
  upsConfigured?: boolean
  fedexConfigured?: boolean
  uspsConfigured?: boolean
  carrierAccounts?: string[]
  shippoMessages?: any[]
} | null>(null)

/**
 * Filter Shippo messages to only show important/actionable ones in the UI.
 * Errors (Hard severity, Invalid) are highlighted in red; others in amber.
 */
const importantMessages = computed(() => {
  const msgs = carrierMeta.value?.shippoMessages || []
  return msgs
    .filter((m: any) => {
      const text = String(m.text || '').toLowerCase()
      // Keep hard errors, invalid addresses, and address classification changes
      if (text.includes('hard:') || text.includes('invalid')) return true
      if (text.includes('classification is changed')) return true
      return false
    })
    .map((m: any) => ({
      source: m.source || 'Shippo',
      text: m.text || '',
      isError: /hard:|invalid/i.test(m.text || ''),
    }))
})

const filteredConsignees = computed(() => {
  const term = consigneeQuery.value.trim().toLowerCase()
  const list = consigneeDirectory.value
  if (!term) return list.slice(0, 10)
  return list
    .filter((c) =>
      [c.name, c.email, c.phone, c.organization_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    )
    .slice(0, 10)
})

const selectedRate = computed(() => allRates.value.find((r) => r.rateId === selectedRateId.value) || null)

const US_STATE_ABBREVIATIONS = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

watch(useContactForShipping, (v) => {
  if (v) {
    shipAddress1.value = address1.value
    shipAddress2.value = address2.value
    shipCity.value = city.value
    shipState.value = state.value
    shipZip.value = zip.value
  }
})

watch([address1, address2, city, state, zip], () => {
  if (!useContactForShipping.value) return
  shipAddress1.value = address1.value
  shipAddress2.value = address2.value
  shipCity.value = city.value
  shipState.value = state.value
  shipZip.value = zip.value
})

function onConsigneeQueryInput() {
  selectedConsigneeId.value = null
  selectedConsigneeOrganizationId.value = null
  isCreatingNewConsignee.value = true
  contactName.value = consigneeQuery.value
}

function onConsigneeQueryBlur() {
  setTimeout(() => {
    showConsigneeDropdown.value = false
  }, 120)
}

function confirmSelectedRate() {
  if (!selectedRateId.value) {
    message.value = 'Please select a rate first.'
    return
  }
  showRatesModal.value = false
  message.value = 'Rate selected. Click Purchase Shipping Label when ready.'
}

function selectCreateNewConsignee() {
  selectedConsigneeId.value = null
  selectedConsigneeOrganizationId.value = null
  isCreatingNewConsignee.value = true
  showConsigneeDropdown.value = false
  contactName.value = consigneeQuery.value.trim()
  organization.value = ''
  email.value = ''
  phone.value = ''
  address1.value = ''
  address2.value = ''
  city.value = ''
  state.value = ''
  zip.value = ''
}

function selectExistingConsignee(consignee: {
  id: number
  organization_id: number | null
  name: string | null
  email: string | null
  phone: string | null
  address1: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  shipping_same_as_consignee: boolean | null
  shipping_address1: string | null
  shipping_address2: string | null
  shipping_city: string | null
  shipping_state: string | null
  shipping_zip: string | null
  organization_name: string | null
}) {
  selectedConsigneeId.value = consignee.id
  selectedConsigneeOrganizationId.value = consignee.organization_id ?? null
  isCreatingNewConsignee.value = false
  showConsigneeDropdown.value = false
  consigneeQuery.value = consignee.name || ''
  contactName.value = consignee.name || ''
  organization.value = consignee.organization_name || ''
  email.value = consignee.email || ''
  phone.value = consignee.phone || ''
  address1.value = consignee.address1 || ''
  address2.value = consignee.address2 || ''
  city.value = consignee.city || ''
  state.value = (consignee.state || '').toUpperCase()
  zip.value = consignee.zip || ''
  const same = consignee.shipping_same_as_consignee ?? true
  useContactForShipping.value = same
  shipAddress1.value = same ? address1.value : (consignee.shipping_address1 || '')
  shipAddress2.value = same ? address2.value : (consignee.shipping_address2 || '')
  shipCity.value = same ? city.value : (consignee.shipping_city || '')
  shipState.value = same ? state.value : ((consignee.shipping_state || '').toUpperCase())
  shipZip.value = same ? zip.value : (consignee.shipping_zip || '')
}

async function loadConsigneeDirectory() {
  const { data } = await supabase
    .schema('app' as any)
    .from('contacts')
    .select('*, organizations(name)')
    .order('name')
  consigneeDirectory.value = (data || []).map((row: any) => ({
    id: row.id,
    organization_id: row.organization_id ?? null,
    name: row.name ?? null,
    email: row.email ?? null,
    phone: row.phone ?? null,
    address1: row.address1 ?? null,
    address2: row.address2 ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    zip: row.zip ?? null,
    shipping_same_as_consignee: row.shipping_same_as_consignee ?? null,
    shipping_address1: row.shipping_address1 ?? null,
    shipping_address2: row.shipping_address2 ?? null,
    shipping_city: row.shipping_city ?? null,
    shipping_state: row.shipping_state ?? null,
    shipping_zip: row.shipping_zip ?? null,
    organization_name: row.organizations?.name ?? null,
  }))
}

async function resolveOrganizationIdForCheckoutContext(): Promise<number | null> {
  if (selectedConsigneeOrganizationId.value) return selectedConsigneeOrganizationId.value
  const orgName = organization.value.trim()
  if (!orgName) return null
  const { data: existingOrg, error: orgLookupError } = await supabase
    .schema('app' as any)
    .from('organizations')
    .select('id')
    .ilike('name', orgName)
    .limit(1)
    .maybeSingle()
  if (orgLookupError) throw orgLookupError
  if (existingOrg?.id) return Number(existingOrg.id)
  const { data: createdOrg, error: orgCreateError } = await supabase
    .schema('app' as any)
    .from('organizations')
    .insert({ name: orgName, type: 'customer' })
    .select('id')
    .single()
  if (orgCreateError) throw orgCreateError
  return Number(createdOrg.id)
}

async function ensureConsigneeContactForCheckoutContext(
  organizationId: number | null,
): Promise<number | null> {
  if (selectedConsigneeId.value) return selectedConsigneeId.value
  const resolvedName = contactName.value.trim() || consigneeQuery.value.trim()
  const resolvedEmail = email.value.trim()
  const resolvedPhone = phone.value.trim()
  const hasAnyConsigneeInput = Boolean(
    resolvedName ||
      resolvedEmail ||
      resolvedPhone ||
      address1.value.trim() ||
      city.value.trim() ||
      state.value.trim() ||
      zip.value.trim(),
  )
  if (!hasAnyConsigneeInput) return null
  const normalizedName = resolvedName.toLowerCase()
  const normalizedEmail = resolvedEmail.toLowerCase()
  const normalizedPhone = resolvedPhone.replace(/\D/g, '')
  const existingDirectoryMatch = consigneeDirectory.value.find((c) => {
    const cName = String(c.name || '').trim().toLowerCase()
    const cEmail = String(c.email || '').trim().toLowerCase()
    const cPhone = String(c.phone || '').replace(/\D/g, '')
    const emailMatch = Boolean(normalizedEmail && cEmail && cEmail === normalizedEmail)
    const phoneMatch = Boolean(normalizedPhone && cPhone && cPhone === normalizedPhone)
    const nameMatch = Boolean(normalizedName && cName && cName === normalizedName)
    return emailMatch || phoneMatch || (nameMatch && c.organization_id === organizationId)
  })
  if (existingDirectoryMatch?.id) {
    selectedConsigneeId.value = existingDirectoryMatch.id
    selectedConsigneeOrganizationId.value = existingDirectoryMatch.organization_id ?? null
    return existingDirectoryMatch.id
  }
  const { data: createdContact, error: contactCreateError } = await supabase
    .schema('app' as any)
    .from('contacts')
    .insert({
      organization_id: organizationId,
      name: resolvedName || null,
      email: resolvedEmail || null,
      phone: resolvedPhone || null,
      address1: address1.value.trim() || null,
      address2: address2.value.trim() || null,
      city: city.value.trim() || null,
      state: state.value.trim().toUpperCase() || null,
      zip: zip.value.trim() || null,
      shipping_same_as_consignee: useContactForShipping.value,
      shipping_address1: useContactForShipping.value ? null : (shipAddress1.value.trim() || null),
      shipping_address2: useContactForShipping.value ? null : (shipAddress2.value.trim() || null),
      shipping_city: useContactForShipping.value ? null : (shipCity.value.trim() || null),
      shipping_state: useContactForShipping.value
        ? null
        : (shipState.value.trim().toUpperCase() || null),
      shipping_zip: useContactForShipping.value ? null : (shipZip.value.trim() || null),
    })
    .select('id')
    .single()
  if (contactCreateError) throw contactCreateError
  const newContactId = Number(createdContact.id)
  selectedConsigneeId.value = newContactId
  selectedConsigneeOrganizationId.value = organizationId
  try {
    await loadConsigneeDirectory()
  } catch {
    // Non-fatal for label purchase flow.
  }
  return newContactId
}

async function syncCheckoutContext(
  checkoutId: number,
  organizationId: number | null,
  contactId: number | null,
) {
  try {
    await supabase
      .schema('app' as any)
      .from('checkouts')
      .update({
        organization_id: organizationId,
        contact_id: contactId,
        project_name: projectName.value.trim() || null,
        purpose: purpose.value || null,
        watchlist: watchlist.value,
        updated_at: new Date().toISOString(),
      })
      .eq('id', checkoutId)
  } catch {
    // Non-fatal: shipping purchase should continue even if this enrichment fails.
  }
}

onMounted(async () => {
  if (props.checkoutId) {
    try {
      const { data: checkoutData } = await supabase
        .schema('app' as any)
        .from('checkouts')
        .select('project_name,purpose,watchlist')
        .eq('id', props.checkoutId)
        .maybeSingle()
      if (checkoutData) {
        projectName.value = String(checkoutData.project_name || '')
        const checkoutPurpose = String(checkoutData.purpose || '').toLowerCase()
        if (
          checkoutPurpose === 'poc' ||
          checkoutPurpose === 'test' ||
          checkoutPurpose === 'tech_eval' ||
          checkoutPurpose === 'maintenance'
        ) {
          purpose.value = checkoutPurpose
        }
        watchlist.value = Boolean((checkoutData as any).watchlist)
      }
    } catch {
      // Non-fatal preload issue.
    }
  }

  // Fetch contact/shipping info from the latest reservation for this serial
  const { data } = await supabase
    .from('reservations_v')
    .select(
      'contact_name, contact_email, contact_phone, address1:contact_address1, city:contact_city, state:contact_state, zip:contact_zip, purpose, shipping_same_as_contact, shipping_address1, shipping_city, shipping_state, shipping_zip',
    )
    .eq('equipment_serial', props.serial)
    .order('start_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (data) {
    contactName.value = data.contact_name || ''
    consigneeQuery.value = data.contact_name || ''
    email.value = data.contact_email || ''
    phone.value = data.contact_phone || ''
    address1.value = data.address1 || ''
    city.value = data.city || ''
    state.value = data.state || ''
    zip.value = data.zip || ''
    const reservationPurpose = String(data.purpose || '').toLowerCase()
    if (reservationPurpose === 'poc' || reservationPurpose === 'test' || reservationPurpose === 'tech_eval') {
      purpose.value = reservationPurpose
    }
    useContactForShipping.value = !!data.shipping_same_as_contact
    shipAddress1.value =
      data.shipping_address1 || (useContactForShipping.value ? address1.value : '')
    shipAddress2.value = useContactForShipping.value ? address2.value : ''
    shipCity.value = data.shipping_city || (useContactForShipping.value ? city.value : '')
    shipState.value = data.shipping_state || (useContactForShipping.value ? state.value : '')
    shipZip.value = data.shipping_zip || (useContactForShipping.value ? zip.value : '')
  }

  // Fetch warehouse ship-from address from app.settings
  try {
    const wh = await getWarehouseAddress()
    warehouseAddress.value = wh as ShippingAddress
  } catch { /* non-fatal — buildAddresses() has inline fallback */ }

  try {
    await loadConsigneeDirectory()
  } catch { /* non-fatal for modal usage */ }

  // Fetch equipment type specs (weight + box dimensions) from the catalog
  try {
    const { data: typeData } = await supabase
      .from('equipment_types_v')
      .select('specs')
      .eq('make', props.make)
      .eq('model', props.model)
      .limit(1)
      .maybeSingle()
    if (typeData?.specs) {
      const s = typeData.specs as Record<string, any>
      // DB stores weight in oz, dimensions in inches
      if (s.shippingWeightOz) weightLbs.value = +(Number(s.shippingWeightOz) / 16).toFixed(1)
      if (s.shippingDepthIn) lengthIn.value = +Number(s.shippingDepthIn).toFixed(1)
      if (s.shippingWidthIn) widthIn.value = +Number(s.shippingWidthIn).toFixed(1)
      if (s.shippingHeightIn) heightIn.value = +Number(s.shippingHeightIn).toFixed(1)
    }
  } catch { /* non-fatal — use defaults */ }
})

function buildAddresses() {
  const to: ShippingAddress = {
    contact_name: contactName.value || undefined,
    company: organization.value || undefined,
    email: email.value || undefined,
    phone: phone.value || undefined,
    line1: (useContactForShipping.value ? address1.value : shipAddress1.value) || '',
    line2: useContactForShipping.value
      ? (address2.value || undefined)
      : (shipAddress2.value || undefined),
    city: (useContactForShipping.value ? city.value : shipCity.value) || '',
    state: (useContactForShipping.value ? state.value : shipState.value) || undefined,
    postal_code: (useContactForShipping.value ? zip.value : shipZip.value) || undefined,
    country: 'US',
  }
  // Warehouse origin loaded from app.settings (with inline fallback)
  const from: ShippingAddress = warehouseAddress.value ?? {
    contact_name: 'Routr Warehouse',
    company: 'Routr',
    line1: '1 Market St',
    city: 'San Francisco',
    state: 'CA',
    postal_code: '94105',
    country: 'US',
  }
  return { from, to }
}

/**
 * Apply the carrier filter to allRates and auto-select cheapest visible rate.
 */
function applyFilter() {
  const filtered = carrierFilter.value
    ? allRates.value.filter((r) => r.carrier === carrierFilter.value)
    : allRates.value
  rates.value = filtered
  if (filtered.length) {
    const cheapest = filtered.slice().sort((a, b) => a.costCents - b.costCents)[0]
    selectedRateId.value = cheapest?.rateId || ''
    message.value = `${filtered.length} rate${filtered.length > 1 ? 's' : ''} — auto-selected ${cheapest?.carrier} ${cheapest?.service} ($${(cheapest?.costCents / 100).toFixed(2)})`
  } else {
    selectedRateId.value = ''
    message.value = 'No rates match this filter.'
  }
}

watch(carrierFilter, () => applyFilter())

async function getRates() {
  try {
    error.value = ''
    message.value = ''
    rates.value = []
    allRates.value = []
    selectedRateId.value = ''
    gettingRates.value = true
    showRatesModal.value = true
    const { from, to } = buildAddresses()
    const parcel: ShippingParcel = {
      weight_kg: weightKg.value,
      length_cm: lengthCm.value,
      width_cm: widthCm.value,
      height_cm: heightCm.value,
    }
    const res = await fetchShippingRates({ from, to, parcel })
    allRates.value = res?.rates || []
    carrierMeta.value = res?.meta || null
    showRates.value = true
    applyFilter()
    if (!allRates.value.length) {
      message.value = 'No rates found.'
    }
  } catch (e: any) {
    error.value = e?.message || String(e)
  } finally {
    gettingRates.value = false
  }
}

async function purchase() {
  try {
    if (!selectedRateId.value) {
      if (rates.value.length) {
        selectedRateId.value =
          rates.value.slice().sort((a, b) => a.costCents - b.costCents)[0]?.rateId || ''
      }
    }
    if (!selectedRateId.value) {
      error.value = 'Please select a rate.'
      return
    }
    purchasing.value = true
    error.value = ''
    message.value = ''
    shippingModeWarning.value = ''
    const resolvedOrganizationId = await resolveOrganizationIdForCheckoutContext()
    const resolvedConsigneeContactId = await ensureConsigneeContactForCheckoutContext(
      resolvedOrganizationId,
    )
    if (props.checkoutId) {
      await syncCheckoutContext(props.checkoutId, resolvedOrganizationId, resolvedConsigneeContactId)
    }
    const { from, to } = buildAddresses()
    const idempotencyKey = crypto.randomUUID()
    // Purchase outbound label AND create a scan-based (pay-on-use) return label.
    // The return label uses Shippo's `is_return: true` flag which means:
    //   - The return label is FREE to generate and print
    //   - No charge until the carrier physically scans the label at pickup/drop-off
    //   - The label can be enclosed in the outbound shipment for the recipient
    //   - UPS labels valid 100 days, USPS 365 days, FedEx 2 years
    const res = await purchaseShippingLabel({
      type: 'outbound',
      rateId: selectedRateId.value,
      from,
      to,
      parcel: {
        weight_kg: weightKg.value,
        length_cm: lengthCm.value,
        width_cm: widthCm.value,
        height_cm: heightCm.value,
      },
      checkoutId: props.checkoutId,
      equipmentId: props.equipmentId,
      idempotencyKey,
      createReturn: true,
      projectName: projectName.value.trim() || undefined,
      purpose: purpose.value,
      consigneeContactId: resolvedConsigneeContactId ?? selectedConsigneeId.value ?? undefined,
      shippingSameAsConsignee: useContactForShipping.value,
    })
    trackingNumber.value = res?.shipment?.tracking_number || ''
    labelUrl.value = res?.shipment?.label_url || ''
    outboundCostCents.value = res?.shipment?.cost_cents ?? null
    // Populate return label fields from the scan-based return label
    returnTracking.value = res?.return_shipment?.tracking_number || ''
    returnLabelUrl.value = res?.return_shipment?.label_url || ''
    returnCostCents.value = res?.return_shipment?.cost_cents ?? null
    const debug = res?.shipment?.meta?.debug || {}
    const labelSource = String(debug?.labelSource || '').toLowerCase()
    const provider = String(debug?.provider || '').toLowerCase()
    if (
      labelSource === 'stub' ||
      labelSource === 'dummy' ||
      provider === 'mock' ||
      labelUrl.value.includes('dummylabel.example')
    ) {
      shippingModeWarning.value =
        'Label generated in fallback/test mode (stub/mock), not a live carrier label. Set a live SHIPPO_API_KEY in Supabase secrets and redeploy shipping functions.'
    }
    // Fallback: synthesize a placeholder label only when Shippo returns a dummy/empty URL
    if (!labelUrl.value || labelUrl.value.includes('dummylabel.example')) {
      const content = `Tracking: ${trackingNumber.value}\nPlaceholder label — Shippo did not return a real label PDF.`
      const blob = new Blob([content], { type: 'text/plain' })
      labelUrl.value = URL.createObjectURL(blob)
    }
    message.value = returnTracking.value
      ? 'Outbound label + scan-based return label created. Enclose the return label in the outbound shipment. Return label is only charged when the carrier scans it.'
      : 'Outbound label created. Return label could not be generated — you may need to create one manually.'
    completed.value = true
    try {
      window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT))
    } catch {}
    try {
      window.dispatchEvent(
        new CustomEvent('roadmap-status', { detail: { status: 'outbound_label' } }),
      )
    } catch {}
    showResult.value = true
  } catch (e: any) {
    error.value = e?.message || String(e)
  } finally {
    purchasing.value = false
  }
}

async function save() {
  const resolvedOrganizationId = await resolveOrganizationIdForCheckoutContext()
  const resolvedConsigneeContactId = await ensureConsigneeContactForCheckoutContext(
    resolvedOrganizationId,
  )
  if (props.checkoutId) {
    await syncCheckoutContext(props.checkoutId, resolvedOrganizationId, resolvedConsigneeContactId)
    try {
      window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT))
    } catch {}
  }
  // Backward-compatible: if no rates yet, fetch; otherwise purchase
  if (!rates.value.length) {
    await getRates()
    return
  }
  await purchase()
}

function openLabel() {
  if (labelUrl.value) {
    try {
      window.open(labelUrl.value, '_blank')
    } catch {}
  }
}

function printUrl(url: string){
  try {
    const w = window.open(url, '_blank')
    if (!w) return
    const onload = () => { try { w.focus(); w.print(); } catch {} }
    if (w.document?.readyState === 'complete') onload();
    else w.addEventListener('load', onload)
  } catch {}
}
</script>

<style scoped>
.rates-pop-enter-active {
  transition:
    opacity 220ms ease,
    transform 460ms cubic-bezier(0.2, 1.1, 0.3, 1);
}

.rates-pop-leave-active {
  transition:
    opacity 220ms ease,
    transform 420ms cubic-bezier(0.2, 1, 0.3, 1);
}

.rates-pop-enter-from,
.rates-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-42px);
}
</style>
